import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  AdminClientCredits,
  AdminCreditPackage,
  ClientQuotaDetails,
  ClientQuotaRow,
  Organisation,
  OrganisationService,
} from '../../../services/organisation.service';

@Component({
  selector: 'app-client-quota-dialog',
  templateUrl: './client-quota-dialog.component.html',
  styleUrl: './client-quota-dialog.component.scss',
})
export class ClientQuotaDialogComponent implements OnInit {
  @Input() data?: Organisation;

  details?: ClientQuotaDetails;
  loading = false;
  saving = false;
  changed = false;

  /** Credit view (plan credits, used, bought, gifted) — what the customer is
   *  billed in. The token limit below stays for the technical gate. */
  credits?: AdminClientCredits;
  creditsLoading = false;
  granting = false;
  /** Second step of the gift: the summary is shown, waiting for confirm. */
  confirmingGift = false;
  /** One per submit — the backend books a repeated id only once. */
  private giftRequestId = this.newRequestId();

  giftForm = this.fb.group({
    credits: [null as number | null, [Validators.required, Validators.min(1), Validators.max(1_000_000)]],
    note: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(1000)]],
    customer_message: ['', [Validators.maxLength(1000)]],
    notify_customer: [true],
  });

  form = this.fb.group({
    api_usage_id: [''],
    token_quota_limit: [0, [Validators.required, Validators.min(-1)]],
    note: [''],
  });

  constructor(
    public dialogRef: NbDialogRef<ClientQuotaDialogComponent>,
    private fb: FormBuilder,
    private organisationService: OrganisationService,
    private toastr: NbToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
    this.loadCredits();
  }

  // ─── Credits ───────────────────────────────────────────────────────────────

  loadCredits(): void {
    if (!this.data?.id) {
      return;
    }
    this.creditsLoading = true;
    this.organisationService.getClientCredits(this.data.id).subscribe({
      next: (credits) => {
        this.credits = credits;
        this.creditsLoading = false;
        this.giftForm.controls.credits.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(credits.max_gift_credits),
        ]);
        this.giftForm.controls.credits.updateValueAndValidity();
      },
      error: () => {
        this.creditsLoading = false;
        this.toastr.danger(this.t('loadError'), this.t('errorTitle'));
      },
    });
  }

  /** First click: validate and show the summary; the second books. */
  requestGift(): void {
    if (this.giftForm.invalid) {
      this.giftForm.markAllAsTouched();
      return;
    }
    this.confirmingGift = true;
  }

  cancelGift(): void {
    this.confirmingGift = false;
  }

  confirmGift(): void {
    if (!this.data?.id || this.granting || this.giftForm.invalid) {
      return;
    }
    const v = this.giftForm.getRawValue();
    this.granting = true;
    this.organisationService
      .grantClientCredits(this.data.id, {
        credits: Number(v.credits),
        request_id: this.giftRequestId,
        note: (v.note || '').trim(),
        customer_message: (v.customer_message || '').trim() || undefined,
        notify_customer: !!v.notify_customer,
      })
      .subscribe({
        next: (res) => {
          this.granting = false;
          this.confirmingGift = false;
          this.credits = res.credits;
          this.changed = true;
          this.giftRequestId = this.newRequestId();
          this.giftForm.reset({ credits: null, note: '', customer_message: '', notify_customer: true });
          const credits = this.formatNumber(Number(v.credits));
          this.toastr.success(
            this.t(v.notify_customer ? (res.mail_sent ? 'giftDoneMail' : 'giftDoneMailFailed') : 'giftDone', { credits }),
            this.t('giftDoneTitle')
          );
        },
        error: () => {
          // Same request id on retry: a gift that did land is not booked twice.
          this.granting = false;
          this.toastr.danger(this.t('giftError'), this.t('errorTitle'));
        },
      });
  }

  sourceLabel(p: AdminCreditPackage): string {
    return this.t(`source.${p.purchase_source}`);
  }

  formatCredits(value?: number | null): string {
    if (value === undefined || value === null) {
      return '-';
    }
    if (value === -1) {
      return this.t('unlimited');
    }
    return value.toLocaleString('de-DE', { maximumFractionDigits: 1 });
  }

  formatDay(value?: string | null): string {
    return value ? new Date(value).toLocaleDateString('de-DE') : '-';
  }

  trackPackage = (_: number, p: AdminCreditPackage) => p.id;

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(`pages.admin.clientCredits.${key}`, params);
  }

  private newRequestId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
  }

  // ─── Technical token limit (unchanged) ─────────────────────────────────────

  load(): void {
    if (!this.data?.id) {
      return;
    }

    this.loading = true;
    this.organisationService.getClientQuota(this.data.id).subscribe({
      next: (details) => {
        this.details = details;
        this.selectRow(details.quota_rows[0]);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastr.danger('Quota-Daten konnten nicht geladen werden.', 'Fehler');
      },
    });
  }

  get selectedRow(): ClientQuotaRow | undefined {
    const id = this.form.value.api_usage_id;
    return this.details?.quota_rows.find((row) => row.api_usage_id === id);
  }

  selectRow(row?: ClientQuotaRow): void {
    if (!row) {
      return;
    }
    this.form.patchValue({
      api_usage_id: row.api_usage_id,
      token_quota_limit: row.active_token_quota_limit,
      note: '',
    });
  }

  onRowChange(apiUsageId: string): void {
    this.selectRow(this.details?.quota_rows.find((row) => row.api_usage_id === apiUsageId));
  }

  previewRemaining(): number {
    const row = this.selectedRow;
    const limit = Number(this.form.value.token_quota_limit);
    if (!row || !row.enforce_token_quota || limit === -1 || Number.isNaN(limit)) {
      return -1;
    }
    return Math.max(0, limit - row.token_usage_count);
  }

  isChanged(): boolean {
    const row = this.selectedRow;
    return !!row && Number(this.form.value.token_quota_limit) !== row.active_token_quota_limit;
  }

  save(): void {
    if (this.form.invalid || !this.data?.id || !this.selectedRow) {
      this.form.markAllAsTouched();
      return;
    }

    const tokenQuotaLimit = Number(this.form.value.token_quota_limit);
    this.saving = true;
    this.organisationService
      .updateClientQuota(this.data.id, {
        api_usage_id: this.form.value.api_usage_id || undefined,
        token_quota_limit: tokenQuotaLimit,
        note: this.form.value.note || undefined,
      })
      .subscribe({
        next: (details) => {
          this.details = details;
          const updatedRow =
            details.quota_rows.find((row) => row.api_usage_id === this.form.value.api_usage_id) ||
            details.quota_rows[0];
          this.selectRow(updatedRow);
          this.saving = false;
          this.changed = true;
          this.toastr.success('Client-Quota wurde aktualisiert.', 'Gespeichert');
        },
        error: () => {
          this.saving = false;
          this.toastr.danger('Client-Quota konnte nicht gespeichert werden.', 'Fehler');
        },
      });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }

  formatNumber(value?: number | null): string {
    if (value === undefined || value === null) {
      return '-';
    }
    if (value === -1) {
      return 'Unlimited';
    }
    return value.toLocaleString('de-DE');
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }
    return new Date(value).toLocaleString('de-DE');
  }
}
